import { useTranslation } from 'react-i18next';

import { ChartKey, type ChartKeySwatchProps } from '@/components/charts/legend';

import { useDashboardLocale } from './dashboardLocale';
import {
    type KeySwatch,
    type PelletKeyInput,
    pelletKeyEntries,
    type TargetKeyInput,
    targetKeyEntries,
} from './penChartKeyEntries';

import styles from './PenChartKey.module.scss';

type PenChartKeyProps =
    | ({ kind: 'target' } & TargetKeyInput)
    | ({ kind: 'pellet' } & PelletKeyInput);

/**
 * The key under a bar chart: what its reference lines are and what a recoloured bar means.
 * The legend names only the series, so without it a warned or banded bar reads as a third
 * series, and an unlabelled dashed line as nothing.
 */
export function PenChartKey(props: PenChartKeyProps) {
    const { t } = useTranslation();
    const locale = useDashboardLocale();
    const entries =
        props.kind === 'target'
            ? targetKeyEntries(props, t, locale)
            : pelletKeyEntries(props, t, locale);

    return (
        <ChartKey
            className={styles.key}
            nowrapLabels={false}
            entries={entries.map((entry) => ({
                id: entry.id,
                label: entry.label,
                swatch: entry.swatch ? keySwatch(entry.swatch) : undefined,
            }))}
        />
    );
}

/** A tier is drawn like the bar it explains; a reference line like the dashed line. */
function keySwatch(swatch: KeySwatch): ChartKeySwatchProps | ChartKeySwatchProps[] {
    if (swatch.kind === 'fill') {
        return { shape: 'square', color: swatch.color, outline: swatch.outline };
    }
    return swatch.colors.map((color) => ({ shape: 'dashed', color }));
}
